import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileCheck, AlertTriangle } from 'lucide-react';
import { slice } from '../../utils';
import { typeProcess } from '../../components/type';

interface FileUploadProps {
    onFileSelect: (file: File) => void;
    processType?: typeProcess;
    isUploading?: boolean;
    onUploadStateChange?: (isUploading: boolean) => void;
    selectedFile?: File | null;
}

interface AlertState {
    type: 'success' | 'warning' | '';
    message: string;
    isActive: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, processType, isUploading = false, onUploadStateChange, selectedFile }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [alert, setAlert] = useState<AlertState>({
        type: '',
        message: '',
        isActive: false
    });

    // Sync fileName with selectedFile prop (for pasted files)
    useEffect(() => {
        console.log('[FileUpload] selectedFile changed:', selectedFile?.name);
        if (selectedFile) {
            setFileName(selectedFile.name);
            console.log('[FileUpload] fileName updated to:', selectedFile.name);
        }
    }, [selectedFile]);

    const clearAlert = useCallback(() => {
        setTimeout(() => {
            setAlert({ type: '', message: '', isActive: false });
        }, 6000);
    }, []);



    const isValidFile = useCallback((file: File): boolean => {
        const typeConfig: Record<typeProcess, { types: string[]; maxSize: number; alertMessage: string }> = {
            AI: {
                types: ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'],
                maxSize: 5 * 1024 * 1024,
                alertMessage: t('views.processInterview.validFilleAi'),
            },
            HUMAN: {
                types: [
                    'application/pdf',
                    'text/plain',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                ],
                maxSize: 10 * 1024 * 1024,
                alertMessage: t('views.processInterview.validFille'),
            },
            IMAGE: {
                types: ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
                maxSize: 5 * 1024 * 1024,
                alertMessage: t('views.processInterview.validFilleImage'),
            },
            MANUAL: {
                types: [],
                maxSize: 0,
                alertMessage: t('views.processInterview.validSize'),
            },
        };

        const currentProcess = (processType ?? 'HUMAN') as typeProcess;
        const config = typeConfig[currentProcess];

        if (!config.types.includes(file.type) || file.size > config.maxSize) {
            setAlert({
                type: 'warning',
                message: config.alertMessage,
                isActive: true,
            });
            clearAlert();
            return false;
        }

        return true;
    }, [processType, t, clearAlert]);


    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && isValidFile(file)) {
            setFileName(file.name);
            onUploadStateChange?.(true);
            onFileSelect(file);
            setAlert({
                type: 'success',
                message: t('views.processInterview.fileUploaded'),
                isActive: true
            });
            clearAlert();
            // Simulate upload completion after file is selected
            setTimeout(() => onUploadStateChange?.(false), 500);
        }
    }, [onFileSelect, isValidFile, t, clearAlert, onUploadStateChange]);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!isDragging) {
            setIsDragging(true);
        }
    }, [isDragging]);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            if (isValidFile(file)) {
                setFileName(file.name);
                onUploadStateChange?.(true);
                onFileSelect(file);
                setAlert({
                    type: 'success',
                    message: t('views.processInterview.fileUploaded'),
                    isActive: true
                });
                clearAlert();
                // Simulate upload completion after file is selected
                setTimeout(() => onUploadStateChange?.(false), 500);
            }
        }
    }, [onFileSelect, isValidFile, t, clearAlert, onUploadStateChange]);

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const acceptedTypes: any = {
        AI: ".pdf,.jpg,.jpeg,.png,.gif,.webp",
        IMAGE: ".pdf,.jpg,.jpeg,.png,.webp,.svg",
        HUMAN: ".pdf,.txt,.docx",
        MANUAL: "",
    };

    const getAcceptedFileTypes = (type: typeProcess) => acceptedTypes[type] || acceptedTypes.DEFAULT;

    const fileDescriptionMap: Record<typeProcess, string> = {
        AI: "PDF, JPG, PNG, SVG (max. 5MB)",
        IMAGE: "PDF, JPG, PNG, SVG (max. 5MB)",
        HUMAN: "PDF, TXT, DOCX (max. 10MB)",
        MANUAL: "",
    };

    const displayText = fileDescriptionMap[processType ?? "AI"] || t('views.processInterview.validSize');


    return (
        <div className="d-flex justify-content-center align-items-center w-100">
            <div
                className={`position-relative w-75 p-4 mt-3 mx-3 text-center rounded border file-upload-dropzone
          ${isDragging ? 'file-upload-dropzone--active shadow-sm' : ''}`}
                style={{ borderStyle: 'dashed', transition: 'all 0.2s ease-in-out', cursor: 'pointer' }}
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
            >
                {(selectedFile || fileName) && (
                    <div className="d-flex align-items-center justify-content-center my-3 text-success overflow-hidden">
                        <FileCheck className="me-2" size={20} />
                        <span className="text-sm fw-medium">
                            {(() => {
                                const displayName = selectedFile?.name || fileName;
                                console.log('[FileUpload] Displaying filename:', displayName);
                                return slice(displayName, 70);
                            })()}
                        </span>
                    </div>
                )}

                {alert.isActive && (
                    <div className={`my-3 small d-flex align-items-center justify-content-center ${alert.type === 'warning' ? 'text-warning' : 'text-success'}`}>
                        {alert.type === 'warning' ? (
                            <AlertTriangle size={16} className="me-2" />
                        ) : (
                            <FileCheck size={16} className="me-2" />
                        )}
                        <span>{alert.message}</span>
                    </div>
                )}

                <Upload
                    className={`mx-auto my-3 file-upload-icon ${isDragging ? 'file-upload-icon--active scale-110' : ''}`}
                    size={40}
                    style={{ transition: 'all 0.2s' }}
                />

                <p className="mb-1 small file-upload-text my-2">
                    {t('views.processInterview.dropYourFileHere')}
                    <span className="text-primary ms-1 fw-medium text-decoration-underline">
                        {t('views.processInterview.browse')}
                    </span>
                    <input
                        type="file"
                        className="d-none"
                        accept={getAcceptedFileTypes(processType ?? "AI")}
                        onChange={handleFileInput}
                        ref={fileInputRef}
                    />
                </p>

                <p className="file-upload-text small">
                    {displayText}
                </p>

                {isDragging && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-primary bg-opacity-10 rounded" style={{ pointerEvents: 'none', animation: 'pulse 1s infinite' }} />
                )}
            </div>
        </div>
    );
};

export default FileUpload;
